import React from 'react';
import { Alert, Button, Segmented, Space, Table, Tag, Typography } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { fmtDateTime, stageLabel } from './shared/recruitUi';

const { Text } = Typography;
type Scope = 'upcoming' | 'pending_feedback' | 'all';

/** Interviewer self-service: interviews I'm on and scorecards I still owe. */
const MyInterviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const scope = (sp.get('scope') as Scope) || 'upcoming';

  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.myInterviews(scope),
    queryFn: () => hrmsService.myInterviews(scope),
  });

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="My interviews"
          subtitle="Interviews you are on the panel for, and scorecards you still need to submit"
          primaryActions={
            <Segmented
              value={scope}
              onChange={(v) => setSp({ scope: String(v) }, { replace: true })}
              options={[
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'pending_feedback', label: 'Feedback due' },
                { value: 'all', label: 'All' },
              ]}
            />
          }
        />
      }
    >
      {data && !data.employee_id && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Your user account is not linked to an employee record, so no interviews can be assigned to you." />
      )}
      <Table
        rowKey="id"
        loading={isFetching}
        dataSource={data?.rows || []}
        pagination={{ pageSize: 25 }}
        scroll={{ x: 900 }}
        columns={[
          {
            title: 'When',
            dataIndex: 'scheduled_at',
            width: 170,
            render: (v, r: any) => (
              <Space direction="vertical" size={0}>
                <a onClick={() => navigate(`/hr/interviews/${r.id}`)}>{fmtDateTime(v)}</a>
                {dayjs(v).isSame(dayjs(), 'day') && <Tag color="blue">Today</Tag>}
              </Space>
            ),
          },
          { title: 'Candidate', dataIndex: 'candidate_name', ellipsis: true },
          { title: 'Job', dataIndex: 'job_title', ellipsis: true },
          { title: 'Round', dataIndex: 'round_number', width: 70 },
          {
            title: 'Where',
            key: 'where',
            width: 180,
            ellipsis: true,
            render: (_, r: any) =>
              r.mode === 'Online' && r.meeting_link ? (
                <a href={r.meeting_link} target="_blank" rel="noreferrer">
                  Join online
                </a>
              ) : (
                r.location_text || r.mode
              ),
          },
          { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag> },
          {
            title: 'My feedback',
            key: 'fb',
            width: 160,
            render: (_, r: any) =>
              r.my_feedback_submitted ? (
                <Tag color="green">Submitted</Tag>
              ) : ['Cancelled', 'NoShow'].includes(r.status) ? (
                <Text type="secondary">—</Text>
              ) : (
                <Button size="small" type="primary" icon={<FormOutlined />} onClick={() => navigate(`/hr/interviews/${r.id}/scorecard`)}>
                  Give feedback
                </Button>
              ),
          },
        ]}
      />
    </WorkspaceLayout>
  );
};

export default MyInterviewsPage;
